import { Snackbar, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ToastVariant = 'success' | 'error';

interface ToastContextValue {
  showToast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const value = useContext(ToastContext);
  if (value === null) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return value;
};

interface Props {
  children: React.ReactNode;
}

export const ToastProvider = ({ children }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [variant, setVariant] = useState<ToastVariant>('success');

  const showToast = useCallback(
    (msg: string, toastVariant: ToastVariant = 'success') => {
      setMessage(msg);
      setVariant(toastVariant);
      setIsOpen(true);
    },
    [],
  );

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const isSuccess = variant === 'success';

  const toastMessage = (
    <ToastMessageRoot as="span">
      <StyledToastIcon
        $isSuccess={isSuccess}
        as={isSuccess ? CheckCircle : Close}
        aria-hidden
      />
      <Typography component="span" variant="body2">
        {message}
      </Typography>
    </ToastMessageRoot>
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={isOpen}
        autoHideDuration={4000}
        onClose={handleClose}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </ToastContext.Provider>
  );
};

const ToastMessageRoot = styled(Stack)(({ theme }) => ({
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StyledToastIcon = styled(CheckCircle, {
  shouldForwardProp: (prop) => prop !== '$isSuccess',
})<{ $isSuccess: boolean }>(({ theme, $isSuccess }) => ({
  color: $isSuccess ? theme.palette.success.main : theme.palette.error.main,
  fontSize: 20,
}));
